'use client';

import { useEffect } from 'react';
import { logClientEvent } from '@/lib/log-client-event';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LocaleLink } from '@/components/locale-link';

/**
 * Centered modal shown when an action fails because the user's credit
 * balance is used up. Links to the pricing page, where credits can be
 * topped up (1 € / 100 credits) or the plan upgraded. Shared by every
 * insufficient-credits (402) surface — controlled, so each caller owns
 * its own `open` state.
 */
export function InsufficientCreditsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (open) logClientEvent('paywall_shown', undefined, { kind: 'credits', feature: 'credits' });
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('credits.outOfCreditsDialog.title', 'Keine Credits mehr')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'credits.outOfCreditsDialog.description',
              'Ihr Guthaben ist aufgebraucht. Laden Sie Credits auf (1 € / 100 Credits) oder wechseln Sie zu einem größeren Plan, um fortzufahren.',
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.buttons.close', 'Schließen')}
          </Button>
          <Button asChild>
            <LocaleLink href="/plans" onClick={() => onOpenChange(false)}>
              {t('credits.outOfCreditsDialog.cta', 'Credits kaufen')}
            </LocaleLink>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
